<script setup lang="ts">
import GwTabsInfiniteVue, {
  type GwTabsInfiniteProps,
} from '@/components/lib/GwTabs/GwTabsInfinite.vue'
import { ref, computed } from 'vue'
import { t } from '@/locales/i18n'
import GwMenuNew from '@/components/lib/GwMenuNew/GwMenuNew.vue'
import {
  type DragDropArgs,
  getDragExpectedProps,
} from '@/components/lib/GwDragDropper/GwDragDropperUtils'
import LoaderCircles from '@/components/Loaders/LoaderCircles.vue'
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'
import GwTableInputMenuNew from '@/components/DataTables/v2/GwTableInputMenuNew.vue'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import type { ActiveAnalyzerReport, AnalyzerReport } from '../../../types/api/reports'
import GwBtnGroup, { type GwBtnGroupItem } from '../../lib/GwBtnGroup/GwBtnGroup.vue'

export interface Props {
  activeReport: ActiveAnalyzerReport
  reports: AnalyzerReport[]
  loadingVariant?: GwTabsInfiniteProps['loadingVariant']
  isCreateNew: boolean
}

const props = defineProps<Props>()

const currentMenuItem = ref({} as AnalyzerReport | undefined)
type EditReportAction = {
  id: string
  text: string
  btnProps: {
    icon: string
    disabled?: boolean
    variant?: string
  }
}
const editReportActions = computed(
  () =>
    [
      {
        id: 'duplicate_report',
        text: t('btn.duplicate'),
        btnProps: {
          icon: 'mdi-content-save-move',
        },
      },
      props.reports.length > 1 && {
        id: 'close_report',
        text: t('btn.close_report'),
        btnProps: {
          icon: 'mdi-close-octagon-outline',
          variant: 'btn_text_grey_error',
        },
      },
      props.reports.length > 1 && {
        id: 'close_all_other_reports',
        text: t('btn.close_all_other_reports'),
        btnProps: {
          icon: 'mdi-close-octagon-outline',
          variant: 'btn_text_grey_error',
        },
      },
    ].filter(Boolean) as EditReportAction[],
)
export type actionID = EditReportAction['id'] | 'rename'

const emit = defineEmits([
  'fetchNext:reports',
  'fetchPrevious:reports',
  'patch:report',
  'setActive:report',
  'update:report',
  'update:reports',
  'update:reportsLimit',
  'create:new',
])

const handleDragDrop = (_e: Event, dragArgs: DragDropArgs<AnalyzerReport>) => {
  const { draggedData } = dragArgs
  const draggedItems = [draggedData]

  const {
    did_drop_on_dragged, //
    expected_items,
    expected_ordering_position,
  } = getDragExpectedProps({
    items: props.reports,
    draggedItems,
    dragArgs,
  })

  if (did_drop_on_dragged) return

  emit('update:reports', expected_items)
  emit('patch:report', draggedData.id, { ordering_position: expected_ordering_position })
}
</script>
<template>
  <GwMenuNew
    placement="bottom"
    boxVariant="box_menu_dark"
    closeOnScroll
  >
    <template #opener="{ listeners, methods }">
      <GwTabsInfiniteVue
        :data-tst="`reports_tabs${loadingVariant ? '_loading' : ''}`"
        :options="reports"
        :loadingVariant
        :value="reports.length ? activeReport?.id : ''"
        :gwVirtualListProps="{
          direction: 'horizontal',
          idKey: 'id',
          hasDynamicSize: true,
          dynamicMinSize: 40,
          offset: 0,
          gwScrollContProps: {
            hasResizeObserver: true,
            hasOverflowSurfaces: true,
          },
        }"
        v-bind="$attrs"
        @scrolled:start="emit('fetchPrevious:reports')"
        @scrolled:end="emit('fetchNext:reports')"
        @click="(_e, { id }) => emit('setActive:report', id)"
        @update:renderedItems="count => emit('update:reportsLimit', count)"
        @scroll:update="_e => methods.close()"
        @drag:drop="handleDragDrop"
        @tab:close="item => emit('update:report', 'close_report', item)"
      >
        <template #tabmenu="{ item }">
          <GwTooltipNew
            :closeOnContentHover="true"
            placement="left"
            :tooltip="t('common.manage')"
          >
            <template #opener="{ listeners: tlisteners }">
              <GwBtn
                data-tst="report_tab_menu"
                variant="btn_text_grey_white"
                icon="mdi-dots-vertical"
                class="gw_hvr_content"
                data-gwmenu="noclose"
                v-on="{ ...tlisteners, ...listeners }"
                @click.stop="tlisteners.click"
                @mousedown.stop="() => (currentMenuItem = item as AnalyzerReport)"
              />
            </template>
          </GwTooltipNew>
        </template>
        <template #content_before>
          <GwTooltipNew
            v-if="!isCreateNew"
            :closeOnContentHover="true"
            placement="left"
            :tooltip="t('hhv3.new_report')"
          >
            <template #opener="{ listeners: tlisteners }">
              <GwBtn
                icon="mdi-plus"
                variant="btn_text_primary_primary"
                data-tst="saved_reports_opener"
                style="
                  margin-right: var(--app-gap-1);
                  border-radius: var(--app-rad-2) var(--app-rad-2) 0 0;
                "
                @click="() => emit('create:new')"
                v-on="tlisteners"
              />
            </template>
          </GwTooltipNew>
          <GwBox
            v-else
            style="margin-right: var(--app-gap-1); padding: 0 2.5rem; flex-shrink: 0"
            class="gwtab text-nowrap"
            variant="box_tab"
            :active="true"
          >
            {{ t('hhv3.new_report') }}
          </GwBox>

          <div
            v-if="loadingVariant === 'left'"
            data-tst="loader_left"
            class="loader"
          >
            <LoaderCircles size="small" />
          </div>
        </template>
        <template #content_after>
          <div
            v-if="loadingVariant === 'right'"
            data-tst="loader_right"
            class="loader"
          >
            <LoaderCircles size="small" />
          </div>
        </template>
      </GwTabsInfiniteVue>
    </template>
    <template #content="{ methods: updateMethods }">
      <GwBtnGroup
        style="min-width: clamp(1px, 12rem, 100vw)"
        direction="vertical"
        boxVariant="box_column"
        tstPrefix="report_tab"
        :options="editReportActions as GwBtnGroupItem[]"
        @click="
          (_e: Event, action: GwBtnGroupItem) => {
            emit('update:report', action.id, currentMenuItem)
            updateMethods.close()
          }
        "
      >
        <template #before>
          <GwTableInputMenuNew
            :text="currentMenuItem?.name"
            :modalTitle="t('hhv3.rename_report')"
            :inputLabel="t('hhv3.report_name')"
            tstPrefix="rename_report"
            isPortalNested
            :contentAttrs="{ 'data-gwmenu-noclose': 'true' }"
            @confirm="
              name => {
                emit('update:report', 'rename_report', { ...currentMenuItem, name })
                updateMethods.close()
              }
            "
          >
            <template #opener="{ listeners: renameListeners }">
              <GwBtn
                id="rename"
                data-tst="report_tab_rename_report"
                variant="btn_text_grey_white"
                class="w-100"
                text="Rename"
                icon="mdi-pencil"
                v-on="renameListeners"
              ></GwBtn>
            </template>
          </GwTableInputMenuNew>
        </template>
      </GwBtnGroup>
    </template>
  </GwMenuNew>
</template>

<style lang="scss" scoped>
.loader {
  top: 0;
  z-index: 6;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  padding: 0.25rem;
}
</style>
